/**
 * SessionQuickPickTiles — "What do you need right now?" three-tile row
 * mounted above the preset library on the /app dashboard.
 *
 * Rationale: the app previously asked every new visitor to choose from a
 * 27-preset library — a library-first UX that overwhelms first-timers and
 * delays the first session. Every category leader (Calm, Brain.fm, Endel)
 * leads with a tiny "what do you need right now?" surface because it
 * collapses cold-visit-to-first-play from ~5 clicks to 1.
 *
 * Each tile is a single click into a scientifically-grounded protocol:
 *   - Focus (Beta 14–16 Hz, 25 min) — engaged concentration
 *   - Calm  (Alpha 8–10 Hz, 10 min, breath pacer) — relaxed awareness
 *   - Sleep (Delta 1–4 Hz, 30 min, heartbeat, sleep timer) — drift off
 *
 * Time-of-day "Recommended" badge: the tile most useful for the current
 * hour gets a small glowing pill. Morning (5–16) → Focus. Afternoon
 * (16–20) → Calm. Night (20–5) → Sleep. Subtle — the other two are still
 * one click away.
 *
 * Design: matches the existing rose/cyan/amber accent palette. Each tile
 * has its own gradient so they're visually distinct at a glance without
 * being garish. On hover: subtle lift + glow. On mobile: stacks vertically.
 */
import React from 'react';
import { Target, Leaf, Moon } from 'lucide-react';

const TILES = [
  {
    id: 'focus',
    icon: Target,
    title: 'Focus',
    duration: '25 min',
    description: 'Deep work, one Pomodoro',
    tagline: 'Beta · binaural',
    // Cyan/blue — cognitive / clarity
    accentClass: 'border-cyan-400/30 hover:border-cyan-400/60 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 hover:from-cyan-500/20 hover:to-blue-600/20',
    iconColor: 'text-cyan-300',
    recommendedHours: [5, 11], // 5am - 11am primary, but extended to afternoon below
  },
  {
    id: 'calm',
    icon: Leaf,
    title: 'Calm',
    duration: '10 min',
    description: 'Reset between meetings',
    tagline: 'Alpha · breath pacer',
    // Emerald/green — nature / restoration
    accentClass: 'border-emerald-400/30 hover:border-emerald-400/60 bg-gradient-to-br from-emerald-500/10 to-teal-600/10 hover:from-emerald-500/20 hover:to-teal-600/20',
    iconColor: 'text-emerald-300',
    recommendedHours: [16, 20],
  },
  {
    id: 'sleep',
    icon: Moon,
    title: 'Sleep',
    duration: '30 min',
    description: 'Drift off, auto-fades out',
    tagline: 'Delta · heartbeat',
    // Indigo/violet — night / deep
    accentClass: 'border-indigo-400/30 hover:border-indigo-400/60 bg-gradient-to-br from-indigo-500/10 to-violet-600/10 hover:from-indigo-500/20 hover:to-violet-600/20',
    iconColor: 'text-indigo-300',
    recommendedHours: [20, 5], // wraps over midnight
  },
];

// Returns the tile id that the current hour suggests. Guard wraps over the
// midnight boundary for the Sleep range.
const getRecommendedId = (hour) => {
  if (hour >= 20 || hour < 5) return 'sleep';
  if (hour >= 16) return 'calm';
  return 'focus';
};

export const SessionQuickPickTiles = ({ onStartFocus, onStartCalm, onStartSleep }) => {
  // Compute once per mount — re-running every render would flicker the
  // "Recommended" badge across minute boundaries. A deliberate trade-off.
  const recommendedId = getRecommendedId(new Date().getHours());

  const handlers = {
    focus: onStartFocus,
    calm: onStartCalm,
    sleep: onStartSleep,
  };

  return (
    <section
      className="mb-8"
      data-testid="session-quick-pick-root"
      aria-label="Quick session launcher"
    >
      <h2 className="text-center text-white/70 text-sm font-medium mb-4 tracking-wide">
        What do you need right now?
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {TILES.map((tile) => {
          const Icon = tile.icon;
          const isRecommended = tile.id === recommendedId;
          const onClick = handlers[tile.id];

          return (
            <button
              key={tile.id}
              type="button"
              onClick={onClick}
              data-testid={`quickpick-${tile.id}`}
              className={`relative group flex flex-col items-start gap-2 p-5 rounded-2xl border text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-white/5 ${tile.accentClass}`}
            >
              {isRecommended && (
                <span
                  className="absolute -top-2 right-4 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/90 text-slate-900 shadow-md"
                  data-testid={`quickpick-${tile.id}-recommended`}
                >
                  Recommended
                </span>
              )}

              <div className="flex items-center justify-between w-full">
                <Icon
                  className={`w-8 h-8 ${tile.iconColor} transition-transform duration-300 group-hover:scale-110`}
                  aria-hidden="true"
                />
                <span className="text-white/50 text-xs font-medium tabular-nums">
                  {tile.duration}
                </span>
              </div>

              <div className="mt-1">
                <h3 className="text-white text-xl font-semibold tracking-tight">
                  {tile.title}
                </h3>
                <p className="text-white/60 text-sm mt-0.5 leading-snug">
                  {tile.description}
                </p>
              </div>

              <span className="mt-1 text-white/35 text-xs">
                {tile.tagline}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default SessionQuickPickTiles;
